import { ChevronLeft, ChevronRight } from 'lucide-react'
import pluralize from 'pluralize'

import { cn } from '../../utils/cn'
import { Button } from './button'

interface TablePaginationProps {
  /** Current 1-based page number. */
  page: number
  /** Total number of rows across all pages, from the API's `count`. */
  count: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  onPageChange: (page: number) => void
  /** Noun for the row count, e.g. "certificate holder". Pluralised for you. */
  itemLabel?: string
  className?: string
}

/**
 * Previous/Next pager for a paginated table.
 *
 * Renders nothing when everything fits on one page, so it can be dropped below
 * any table unconditionally.
 *
 * @example
 * ```tsx
 * const { holders, count, hasNextPage, hasPreviousPage } = useCertificateHolders(orgId, page)
 *
 * <TablePagination
 *   page={page}
 *   count={count}
 *   hasNextPage={hasNextPage}
 *   hasPreviousPage={hasPreviousPage}
 *   onPageChange={setPage}
 *   itemLabel="certificate holder"
 * />
 * ```
 */
export function TablePagination({
  page,
  count,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  itemLabel,
  className,
}: TablePaginationProps) {
  if (!hasNextPage && !hasPreviousPage) return null

  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      <p className="text-muted-foreground text-sm">
        Page {page} ({itemLabel ? pluralize(itemLabel, count, true) : count} total)
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!hasPreviousPage}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button variant="outline" size="sm" disabled={!hasNextPage} onClick={() => onPageChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  )
}

// `TablePagination` above pages a server-paginated table, where all we know is
// whether another page exists. `Pagination` below pages a list the client already
// holds in full, so it can offer numbered pages and a row range.

/** Above this many pages the numbers collapse around the current page with ellipses. */
const MAX_NUMBERED_PAGES = 7

/**
 * The page numbers to render, with `'ellipsis'` marking collapsed gaps.
 * Always keeps the first and last page plus a fixed-width window around
 * `page`, so the control doesn't change width as the user pages through.
 */
export function paginationRange(page: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= MAX_NUMBERED_PAGES) {
    return Array.from({ length: Math.max(totalPages, 1) }, (_, index) => index + 1)
  }

  const shown = new Set<number>([1, totalPages, page])
  const window =
    page <= 3
      ? [2, 3, 4]
      : page >= totalPages - 2
        ? [-3, -2, -1].map((o) => totalPages + o)
        : [page - 1, page + 1]
  window.forEach((candidate) => {
    if (candidate > 1 && candidate < totalPages) shown.add(candidate)
  })

  const sorted = [...shown].sort((a, b) => a - b)
  return sorted.flatMap((value, index) =>
    index > 0 && value - sorted[index - 1] > 1 ? ['ellipsis' as const, value] : [value]
  )
}

export interface PaginationProps {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  /** Optional left-hand summary, e.g. "Showing 1–25 of 512 trucks". */
  summary?: React.ReactNode
  className?: string
}

/** Page navigation for client-side paginated tables. Renders nothing for a single page. */
export function Pagination({ page, totalPages, onPageChange, summary, className }: PaginationProps) {
  if (totalPages <= 1) return null

  return (
    <nav
      aria-label="Pagination"
      className={cn('mt-4 flex flex-wrap items-center justify-between gap-3', className)}
    >
      <div className="text-muted-foreground text-xs">{summary}</div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft />
        </Button>

        {paginationRange(page, totalPages).map((entry, index) =>
          entry === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="text-muted-foreground px-1 text-xs">
              …
            </span>
          ) : (
            <Button
              key={entry}
              variant={entry === page ? 'default' : 'outline'}
              size="icon-sm"
              aria-label={`Page ${entry}`}
              aria-current={entry === page ? 'page' : undefined}
              onClick={() => onPageChange(entry)}
            >
              {entry}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Next page"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight />
        </Button>
      </div>
    </nav>
  )
}
